import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation, useParams } from 'react-router-dom';
import './App.css';
import { Analytics } from '@vercel/analytics/react';

const NAME = 'David Martin';
const EMAIL = 'info@adhoctechnology.net';

/* Scrolls to the #hash target when one is present, top of page otherwise.
   React Router does not do this on its own, and the home page capability
   cards link into sections of /services. */
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const target = document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pathname, hash]);

  return null;
}

function Navbar() {
  return (
    <nav className="navbar">
      <ul>
        <li><NavLink to="/" end>Home</NavLink></li>
        <li><NavLink to="/services">Services</NavLink></li>
        <li><NavLink to="/products">Products</NavLink></li>
        <li><NavLink to="/experience">Experience</NavLink></li>
        <li><NavLink to="/about-us">About</NavLink></li>
        <li><NavLink to="/contact-us">Contact</NavLink></li>
      </ul>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="footer">
      Copyright &copy; {new Date().getFullYear()} Ad Hoc Technology - All Rights Reserved.
    </footer>
  );
}

/* The three capability areas are the spine of the site: the home page renders
   them as cards, the services page expands each one into a section. */
const capabilities = [
  {
    id: 'backend',
    title: 'Backend & Full-Stack',
    summary: 'Java and Node.js on both sides of the wire, over whichever data store the job actually calls for.',
    detail: (
      <>
        <p>
          I write client-side and server-side Java and Node.js, and I am comfortable owning a
          feature from the browser through the service layer to the store underneath it.
        </p>
        <ul className="plain-list">
          <li>Client-side and server-side Java</li>
          <li>Node.js services, APIs, and tooling</li>
          <li>Relational modeling and SQL</li>
          <li>NoSQL and document stores</li>
          <li>Key-value and caching backends</li>
          <li>Third-party API design and integration</li>
        </ul>
      </>
    )
  },
  {
    id: 'mobile',
    title: 'Mobile',
    summary: 'Native iOS, native Android, and Flutter when one codebase should serve both stores.',
    detail: (
      <>
        <p>
          I have built mobile applications natively for iOS and Android, and in Flutter where a
          single codebase needs to ship to both platforms. One of my Flutter applications is
          published on the Apple App Store and Google Play, and is in use today.
        </p>
        <ul className="plain-list">
          <li>Flutter, from first screen through store submission</li>
          <li>Native iOS and native Android</li>
          <li>App Store and Google Play release process</li>
          <li>Mobile clients against existing backends</li>
        </ul>
      </>
    )
  },
  {
    id: 'product-se',
    title: 'Product & Sales Engineering',
    summary: 'Fifteen years as a sales engineer and four as a product manager — the work around the code.',
    detail: (
      <>
        <p>
          Building the thing is half of it. I spent four years as a product manager deciding what
          was worth building, and fifteen years as a sales engineer explaining engineering work to
          the people who had to buy it, adopt it, or approve it.
        </p>
        <ul className="plain-list">
          <li>Product planning and roadmap definition</li>
          <li>Demo design and delivery</li>
          <li>Technical evaluations and proofs of concept</li>
          <li>Internal evangelism and onboarding</li>
        </ul>
      </>
    )
  }
];

function Home() {
  return (
    <div className="home">
      <section className="hero">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology Logo" className="hero-logo" />
        <h1>Consulting for teams that turn plans into products.</h1>
        <div className="accent-rule" />
        <p className="hero-lede">
          I build client- and server-side systems in Java and Node.js, mobile apps for iOS,
          Android, and Flutter, and the data layers underneath them. I have also spent fifteen
          years explaining that kind of work to the people who buy it.
        </p>
        <p className="hero-keywords">
          Java &middot; Node.js &middot; iOS &middot; Android &middot; Flutter &middot; SQL
          &middot; NoSQL
        </p>
      </section>

      <div className="capability-grid">
        {capabilities.map((capability) => (
          <Link
            key={capability.id}
            to={`/services#${capability.id}`}
            className="capability-card"
          >
            <h2>{capability.title}</h2>
            <p>{capability.summary}</p>
            <span className="capability-more">Learn more</span>
          </Link>
        ))}
      </div>

      <section className="cta">
        <p>Have something you want to be a big success?</p>
        <Link to="/contact-us" className="cta-link">Start a conversation</Link>
      </section>
    </div>
  );
}

function AboutUs() {
  return (
    <div className="about-us">
      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        About
      </h1>
      <p className="lede">
        I am {NAME}, and Ad Hoc Technology is my consulting practice.
      </p>
      <p>
        I am a generalist by temperament and by track record. Over my career I have written the
        backend and the client, shipped a mobile app to both app stores, managed a product, and
        stood in front of customers as the engineer in the room. That range is the reason to hire
        me: I can take an ambiguous problem, work out what is actually worth building, build it,
        and then explain it convincingly to whomever needs convincing.
      </p>
      <img className="about-image" src="/assets/abstract_about.png" alt="Abstract illustration" />
      <p>
        I also lean hard on modern AI tooling, which has meaningfully compressed how long my
        projects take. Clients get the benefit of that in scope and schedule.
      </p>
      <p>
        The long version &mdash; the specific years, roles, and technologies &mdash; is on
        my <Link to="/experience">Experience</Link> page.
      </p>
      <p>
        Want to learn more? <Link to="/contact-us">Contact me</Link> to start a conversation.
      </p>
    </div>
  );
}

const experience = [
  {
    title: 'Sales Engineering',
    duration: 'Fifteen years',
    body: (
      <p>
        Fifteen years as a sales engineer: technical evaluations, proofs of concept, demos built
        to answer a specific objection, and the follow-through that turns an evaluation into an
        adoption. I am the engineer who can hold the room and still go write the integration
        afterward.
      </p>
    )
  },
  {
    title: 'Feature Flags & Experimentation',
    duration: 'Nearly ten years',
    body: (
      <p>
        Nearly a decade working in feature flags and experimentation &mdash; client-side and
        server-side flagging, JSON-valued flags for rich runtime configuration, impression
        warehousing and reporting, flag lifecycle and cleanup, and the metric and event strategy
        that makes an experiment trustworthy. This work is packaged
        on my <Link to="/services">Services</Link> page.
      </p>
    )
  },
  {
    title: 'Product Management',
    duration: 'Four years',
    body: (
      <p>
        Four years as a product manager, responsible for deciding what was worth building and why.
        It is the discipline I still apply first on any engagement, before writing code.
      </p>
    )
  },
  {
    title: 'Mobile Development',
    duration: 'iOS, Android, Flutter',
    body: (
      <>
        <p>
          Mobile applications built natively for iOS and Android, and in Flutter for cross-platform
          delivery. I have taken a Flutter application all the way through submission to both
          stores; it is published on the Apple App Store and Google Play and in use today.
        </p>
        <p className="todo-note">
          [To add: the app name and its App Store and Google Play links.]
        </p>
      </>
    )
  },
  {
    title: 'Backend & Data',
    duration: 'Java, Node.js, SQL, NoSQL, key-value',
    body: (
      <p>
        Server-side and client-side Java, Node.js services and tooling, and persistence across
        relational databases, NoSQL and document stores, and key-value backends. Most of my work
        has involved choosing among those rather than defaulting to one.
      </p>
    )
  }
];

function Experience() {
  return (
    <div className="experience">
      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        Experience
      </h1>
      <p className="lede">
        The specifics behind the practice.
      </p>

      {experience.map((item) => (
        <section className="experience-item" key={item.title}>
          <h2>{item.title}</h2>
          <p className="experience-duration">{item.duration}</p>
          {item.body}
        </section>
      ))}

      <p><Link to="/contact-us">Contact me</Link> to start a conversation.</p>
    </div>
  );
}

function Services() {
  return (
    <div className="services">
      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        Services
      </h1>
      <p className="lede">
        I am an independent consultant working across the stack. Engagements are scoped to your
        problem &mdash; below are the areas I work in.
      </p>

      {capabilities.map((capability) => (
        <section className="services-section" id={capability.id} key={capability.id}>
          <h2>{capability.title}</h2>
          {capability.detail}
        </section>
      ))}

      <section className="services-section" id="contract-se">
        <h2>Contract Sales Engineer</h2>
        <p>
          Do you have a project you want to be a big success? Does your organization have SEs
          available? If not, you are missing a valuable approach to selling, internally or
          externally.
        </p>
        <ul className="plain-list">
          <li>Product Planning &mdash; hit the mark with proven product management experience</li>
          <li>Demo Planning and Implementation &mdash; produce a high quality, compelling demo experience</li>
          <li>Evangelize &mdash; reach all corners of your organization with the good news</li>
          <li>Implementation &mdash; see your creation adopted</li>
          <li>Onboarding &mdash; see your creation adopted by many</li>
        </ul>
        <p>Contracts can be delivered in as little as four weeks.</p>
      </section>

      <section className="cta">
        <p>Ready to talk specifics?</p>
        <Link to="/contact-us" className="cta-link">Start a conversation</Link>
      </section>
    </div>
  );
}

/* Products are the shipped-software side of the practice. The index page maps
   over this array for its cards; each entry also renders its own detail page
   at /products/:productId, so adding a product is a data change. */
const products = [
  {
    id: 'third-name',
    title: 'Third Name',
    tagline: 'Make friends and have fun by asking questions.',
    summary: 'Write your own questions and let interested parties approach by answering them. Questions are excellent conversation starters.',
    meta: 'thirdname.net',
    metaHref: 'https://thirdname.net',
    visitUrl: 'https://thirdname.net',
    image: '/assets/thirdname.png',
    imageAlt: 'The Third Name home page, explaining how the questions work',
    detail: (
      <>
        <p>
          Making friends is hard, and questions can be excellent conversation starters.
          Write your own questions and let interested parties approach by answering them.
        </p>
        <p>
          You can respond to some or all of their answers in chat, or do nothing &mdash; in
          which case you can&rsquo;t be bothered by the same party again.
        </p>
        <p>
          Slim, easy-to-use interface works on web and mobile. Free for the first 1,000
          profiles.
        </p>
      </>
    ),
  },
  {
    id: 'range-finder',
    title: 'Range Finder',
    tagline: 'Find your precise vocal range by singing to your computer.',
    summary: 'Sing into Range Finder and it reports your lowest and highest notes, in Hz and in cents, so you know exactly which songs are in your range.',
    meta: 'Free \u00b7 macOS and Windows',
    metaHref: 'https://github.com/dbmartin00/range_finder/releases/tag/v1.0.1',
    downloadUrl: 'https://github.com/dbmartin00/range_finder/releases/tag/v1.0.1',
    image: '/assets/range_finder.jpg',
    imageAlt: 'A singer belting into a studio microphone',
    demo: '/assets/range_finder.mp4',
    demoPoster: '/assets/range_finder_poster.jpg',
    demoCaption: 'Range Finder listening, tuning, and reporting a range in about 25 seconds.',
    detail: (
      <>
        <p>
          Ever wanted to sing karaoke and were unsure of which songs? Sing into Range Finder
          and it will tell you precisely:
        </p>
        <pre className="product-readout">
{`============================================================
YOUR RANGE
============================================================

  |···■····■····■··■····■························|
   |       |            |           |            |
  E2      C3           C4          C5           C6

Lowest note   G2      98.00 Hz   (held 2.2s, within 10 cents)
Highest note  C4     261.63 Hz   (held 3.1s, within 4 cents)
Range         G2 to C4  = 1 octave + 5 semitones
Distinct notes sung  5 of 18 in that span

(96s of audio analysed)`}
        </pre>
        <p>
          If I ask ChatGPT for karaoke songs in G2 to C4 it tells me I&rsquo;m a baritone
          and that I should be singing Johnny Cash and Dire Straits and fifty other songs.
        </p>
        <p>
          Just sing and hold a low note, then work up your scale to your highest note. Usage
          is intuitive.
        </p>
      </>
    ),
  },
];

function Products() {
  return (
    <div className="products">
      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        Products
      </h1>
      <p className="lede">
        Software I have built and shipped.
      </p>

      <div className="capability-grid">
        {products.filter((product) => !product.hidden).map((product) => (
          <Link
            key={product.id}
            to={`/products/${product.id}`}
            className="capability-card"
          >
            <h2>{product.title}</h2>
            {product.subtitle && <p className="product-card-subtitle">{product.subtitle}</p>}
            <p>{product.summary}</p>
            <span className="capability-more">Learn more</span>
          </Link>
        ))}
      </div>

      <section className="cta">
        <p>Have something you want to be a big success?</p>
        <Link to="/contact-us" className="cta-link">Start a conversation</Link>
      </section>
    </div>
  );
}

/* One detail page per product. An unknown :productId sends the visitor back to
   the index rather than rendering an empty shell. */
function ProductPage() {
  const { productId } = useParams();
  const product = products.find((candidate) => candidate.id === productId);

  if (!product || product.hidden) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="products">
      <p className="breadcrumb">
        <Link to="/products">&larr; All products</Link>
      </p>

      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        {product.title}
      </h1>
      {product.subtitle && <p className="product-subtitle">{product.subtitle}</p>}
      <p className="product-tagline">{product.tagline}</p>
      {product.meta && (
        <p className="product-meta">
          {product.metaHref ? (
            <a href={product.metaHref} target="_blank" rel="noopener noreferrer">
              {product.meta}
            </a>
          ) : (
            product.meta
          )}
        </p>
      )}

      {product.image && (
        <img className="product-image" src={product.image} alt={product.imageAlt} />
      )}

      {product.detail}

      {product.demos && (
        <div className="demo-list">
          {product.demos.map((demo) => (
            <figure className="demo-figure" key={demo.name}>
              {/* Screenshots are heavy and sit below the fold; let the browser
                  defer them until the visitor scrolls. */}
              <img src={demo.image} alt={`${demo.name}: ${demo.description}`} loading="lazy" />
              <figcaption>
                <strong>{demo.name}</strong> &mdash; {demo.description}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {product.demo && (
        <figure className="product-demo">
          {/* preload="metadata" keeps the 4 MB download off the initial page load;
              the poster frame stands in until the visitor presses play. */}
          <video controls preload="metadata" poster={product.demoPoster} playsInline>
            <source src={product.demo} type="video/mp4" />
            Your browser cannot play this video.{' '}
            <a href={product.demo}>Download the demo</a> instead.
          </video>
          <figcaption>{product.demoCaption}</figcaption>
        </figure>
      )}

      {product.visitUrl && (
        <p>
          <a
            className="cta-link"
            href={product.visitUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit {product.title}
          </a>
        </p>
      )}

      {product.downloadUrl && (
        <p>
          <a
            className="cta-link"
            href={product.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download {product.title}
          </a>
        </p>
      )}

      <section className="cta">
        <p>Want something like this for your team?</p>
        <Link to="/contact-us" className="cta-link">Start a conversation</Link>
      </section>
    </div>
  );
}

function ContactUs() {
  return (
    <div className="contact-us">
      <h1 className="inline-logo-title">
        <img src="/assets/adhoclogo.png" alt="Ad Hoc Technology logo" />
        Contact
      </h1>
      <p className="lede">
        Email me at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and tell me what you are working on.
      </p>
      <p>
        A short description of the problem is enough to start &mdash; I will come back with
        questions and a sense of whether I am the right person for it.
      </p>
      <p>&mdash; {NAME}, Ad Hoc Technology</p>
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="app">
        <ScrollToHash />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:productId" element={<ProductPage />} />
            <Route path="/experience" element={<Experience />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
          </Routes>
        </main>
        <Footer />
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
